<template>
    <AppLayout>
        <template #icon>
            <div class="h-10 w-10 flex items-center justify-center">
                <BookingsHeaderLogo />
            </div>
        </template>
        <template #title>
            {{ reservation.event_title }}
        </template>
        <template #description>
            {{ formatDay(reservation.start) }} •
            {{ formatFromTo(reservation.start, reservation.end) }}
        </template>
        <template #actions>
            <Button
                v-if="isCancellable"
                variant="outline"
                size="sm"
                @click="cancelEvent"
            >
                <X :size="20" :stroke-width="1.75" class="mr-2" />
                {{ t("bookings.cancel_booking") }}
            </Button>

            <Button
                v-if="isCancellable"
                variant="outline"
                size="sm"
                @click="openRescheduleLink"
            >
                <CalendarClock class="mr-2" :size="20" :stroke-width="1.75" />
                {{ t("bookings.reschedule_link") }}
            </Button>
        </template>

        <div class="max-w-5xl py-8 mx-auto">
            <div
                class="mb-6 rounded-xl border-iron-200 bg-white p-6 shadow-sm border"
            >
                <div>
                    <h3 class="mb-3 text-sm font-semibold text-iron-900">
                        {{ t("bookings.contact_information") }}
                    </h3>

                    <div class="gap-3 sm:grid-cols-2 grid">
                        <!-- Email -->
                        <div
                            class="rounded-lg border-iron-100 bg-iron-25 p-4 flex items-center justify-between border"
                        >
                            <div class="min-w-0 gap-3 flex items-center">
                                <span
                                    class="h-10 w-10 bg-blue-100 flex items-center justify-center rounded-full"
                                >
                                    <Mail class="h-5 w-5 text-blue-600" />
                                </span>
                                <div class="min-w-0">
                                    <p
                                        class="text-sm font-semibold text-iron-900"
                                    >
                                        {{ t("bookings.details.email") }}
                                    </p>
                                    <p
                                        class="mt-0.5 text-sm font-medium truncate"
                                    >
                                        <a
                                            class="text-z-primary-600 underline-offset-4 hover:underline"
                                            :href="`mailto:${reservation.email}`"
                                        >
                                            {{ reservation.email }}
                                        </a>
                                    </p>
                                </div>
                            </div>
                            <a
                                :href="`mailto:${reservation.email}`"
                                class="text-iron-400 hover:text-iron-600 shrink-0"
                                :aria-label="t('bookings.details.email')"
                            >
                                <ExternalLink class="h-5 w-5" />
                            </a>
                        </div>

                        <!-- Phone -->
                        <div
                            class="rounded-lg border-iron-100 bg-iron-25 p-4 flex items-center justify-between border"
                        >
                            <div class="min-w-0 gap-3 flex items-center">
                                <span
                                    class="h-10 w-10 bg-green-100 flex items-center justify-center rounded-full"
                                >
                                    <PhoneIcon class="h-5 w-5 text-green-600" />
                                </span>
                                <div class="min-w-0">
                                    <p
                                        class="text-sm font-semibold text-iron-900"
                                    >
                                        {{ t("bookings.details.phone") }}
                                    </p>
                                    <p
                                        class="mt-0.5 text-sm font-medium truncate"
                                    >
                                        <template v-if="reservation.phone">
                                            <a
                                                class="text-z-primary-600 underline-offset-4 hover:underline"
                                                :href="`tel:${reservation.phone}`"
                                            >
                                                {{ reservation.phone }}
                                            </a>
                                        </template>
                                        <span v-else class="text-iron-500"
                                            >-</span
                                        >
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Location -->
                        <div
                            class="rounded-lg border-iron-100 bg-iron-25 p-4 flex items-center justify-between border"
                        >
                            <div class="min-w-0 gap-3 flex items-center">
                                <span
                                    class="h-10 w-10 bg-purple-100 flex items-center justify-center rounded-full"
                                >
                                    <MapPin class="h-5 w-5 text-purple-600" />
                                </span>
                                <div class="min-w-0">
                                    <p
                                        class="text-sm font-semibold text-iron-900"
                                    >
                                        {{ t("bookings.details.location") }}
                                    </p>
                                    <p
                                        class="mt-0.5 text-sm font-medium truncate"
                                    >
                                        <template v-if="meetingUrl">
                                            <a
                                                class="text-z-primary-600 underline-offset-4 hover:underline"
                                                :href="meetingUrl"
                                                target="_blank"
                                                rel="noopener"
                                            >
                                                {{ displayMeetingText }}
                                            </a>
                                        </template>
                                        <span v-else class="text-iron-700">{{
                                            displayMeetingText || "-"
                                        }}</span>
                                    </p>
                                </div>
                            </div>
                            <a
                                v-if="meetingUrl"
                                :href="meetingUrl"
                                target="_blank"
                                rel="noopener"
                                class="text-iron-400 hover:text-iron-600 shrink-0"
                                aria-label="Open location"
                            >
                                <ExternalLink class="h-5 w-5" />
                            </a>
                        </div>

                        <!-- Name -->
                        <div
                            class="rounded-lg border-iron-100 bg-iron-25 p-4 flex items-center justify-between border"
                        >
                            <div class="min-w-0 gap-3 flex items-center">
                                <span
                                    class="h-10 w-10 bg-amber-100 flex items-center justify-center rounded-full"
                                >
                                    <UserIcon class="h-5 w-5 text-amber-600" />
                                </span>
                                <div class="min-w-0">
                                    <p
                                        class="text-sm font-semibold text-iron-900"
                                    >
                                        {{ t("bookings.details.name") }}
                                    </p>
                                    <p
                                        class="mt-0.5 text-sm font-medium text-iron-900 truncate"
                                    >
                                        {{ reservation.firstname }}
                                        {{ reservation.lastname }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-if="
                    reservation.customFieldValues &&
                    reservation.customFieldValues.length
                "
                class="mb-6 rounded-xl border-iron-200 bg-white p-6 shadow-sm border"
            >
                <div>
                    <h3 class="mb-3 text-sm font-semibold text-iron-900">
                        {{ t("bookings.details.custom_fields") }}
                    </h3>

                    <Table class="max-w-xl">
                        <TableHeader>
                            <TableRow>
                                <TableHead class="w-1/3">{{
                                    t("bookings.details.field")
                                }}</TableHead>
                                <TableHead>{{
                                    t("bookings.details.value")
                                }}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow
                                v-for="(
                                    field, idx
                                ) in reservation.customFieldValues"
                                :key="idx"
                            >
                                <TableCell class="font-medium text-iron-900">
                                    {{ field.label || "-" }}
                                </TableCell>
                                <TableCellWithCopy
                                    :value="formatCustomFieldValue(field)"
                                >
                                    {{ formatCustomFieldValue(field) || "-" }}
                                </TableCellWithCopy>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>

            <div
                class="mb-6 rounded-xl border-iron-200 bg-white p-6 shadow-sm border"
            >
                <div class="relative">
                    <ol class="ml-3 border-iron-200 relative border-l">
                        <li
                            v-for="group in displayActivityGroups"
                            :key="group.parent.id"
                            class="mb-8 pl-6 relative"
                        >
                            <span
                                class="h-3 w-3 ring-white absolute top-[2px] -left-[7px] rounded-full ring-2"
                                :class="bulletClass(group.parent.type)"
                            ></span>
                            <div
                                class="mr-2 gap-4 flex items-start justify-between"
                            >
                                <div class="min-w-0">
                                    <div
                                        class="gap-2 flex flex-wrap items-center"
                                    >
                                        <h3
                                            class="text-base font-semibold text-iron-900"
                                        >
                                            {{
                                                translateActivity(
                                                    group.parent.type,
                                                    group.parent.message,
                                                )
                                            }}
                                        </h3>
                                        <Badge
                                            v-if="
                                                group.parent.status === 'fail'
                                            "
                                            variant="destructive"
                                            class="capitalize"
                                            >{{ group.parent.type }}</Badge
                                        >
                                    </div>
                                    <p class="mt-1 text-xs text-iron-500">
                                        {{
                                            formatDayWithTime(
                                                group.parent.createdAt,
                                            )
                                        }}

                                        <br />
                                        <span v-if="group.parent.actorName">{{
                                            group.parent.actorName
                                        }}</span>
                                        <span v-if="group.parent.actorEmail">
                                            &nbsp;
                                            {{ group.parent.actorEmail }}</span
                                        >
                                        <span
                                            v-if="
                                                !group.parent.actorName &&
                                                !group.parent.actorEmail
                                            "
                                            >{{
                                                t("bookings.activity.system")
                                            }}</span
                                        >
                                    </p>
                                    <div
                                        v-if="group.parent.data"
                                        class="mt-3 rounded-lg border-iron-200 bg-white p-3 shadow-sm w-[400px] border"
                                    >
                                        <h4
                                            class="mb-2 text-sm font-semibold text-iron-900"
                                        >
                                            {{
                                                t(
                                                    "bookings.activity.event_data",
                                                )
                                            }}
                                        </h4>
                                        <Accordion
                                            type="single"
                                            collapsible
                                            class="w-[320px]"
                                        >
                                            <AccordionItem
                                                :value="
                                                    'parent-payload-' +
                                                    group.parent.id
                                                "
                                            >
                                                <AccordionTrigger
                                                    class="rounded-md border-iron-200 bg-iron-25 px-3 py-2 text-sm font-medium w-full border text-left"
                                                >
                                                    {{
                                                        t(
                                                            "bookings.activity.payload",
                                                        )
                                                    }}
                                                </AccordionTrigger>
                                                <AccordionContent>
                                                    <pre
                                                        class="mt-2 rounded-md border-iron-200 bg-iron-50 p-2 text-xs leading-relaxed text-iron-800 w-full max-w-full overflow-x-auto border"
                                                        >{{
                                                            pretty(
                                                                group.parent
                                                                    .data,
                                                            )
                                                        }}</pre
                                                    >
                                                </AccordionContent>
                                            </AccordionItem>
                                        </Accordion>
                                    </div>

                                    <div
                                        v-if="group.children.length"
                                        class="mt-3 w-[400px]"
                                    >
                                        <Accordion type="single" collapsible>
                                            <AccordionItem
                                                :value="
                                                    'group-' + group.parent.id
                                                "
                                            >
                                                <AccordionTrigger
                                                    class="rounded-md border-iron-200 bg-iron-25 px-3 py-2 text-sm font-medium border text-left"
                                                >
                                                    <span
                                                        class="gap-2 inline-flex items-center"
                                                    >
                                                        <XCircle
                                                            v-if="
                                                                groupHasFailure(
                                                                    group,
                                                                )
                                                            "
                                                            class="h-4 w-4 text-red-600"
                                                        />
                                                        <CheckCircle
                                                            v-else
                                                            class="h-4 w-4 text-green-600"
                                                        />
                                                        {{
                                                            t(
                                                                "bookings.activity.workflow_actions",
                                                            )
                                                        }}
                                                        ({{
                                                            group.children
                                                                .length
                                                        }})
                                                    </span>
                                                </AccordionTrigger>
                                                <AccordionContent>
                                                    <div
                                                        class="mt-1 space-y-3 rounded-md border-iron-200 bg-iron-50 p-3 border"
                                                    >
                                                        <div
                                                            v-for="child in group.children"
                                                            :key="child.id"
                                                            class="rounded-md border-iron-200 bg-white p-3 shadow-sm border"
                                                        >
                                                            <div
                                                                class="gap-4 flex items-start justify-between"
                                                            >
                                                                <div
                                                                    class="min-w-0"
                                                                >
                                                                    <div
                                                                        class="gap-2 flex flex-wrap items-center"
                                                                    >
                                                                        <span
                                                                            aria-hidden="true"
                                                                        >
                                                                            <XCircle
                                                                                v-if="
                                                                                    isWorkflowFail(
                                                                                        child,
                                                                                    )
                                                                                "
                                                                                class="h-4 w-4 text-red-600"
                                                                            />
                                                                            <CheckCircle
                                                                                v-else
                                                                                class="h-4 w-4 text-green-600"
                                                                            />
                                                                        </span>
                                                                        <h4
                                                                            class="text-sm font-semibold text-iron-900"
                                                                        >
                                                                            {{
                                                                                child.message
                                                                            }}
                                                                        </h4>
                                                                        <Badge
                                                                            :variant="
                                                                                isWorkflowFail(
                                                                                    child,
                                                                                )
                                                                                    ? 'destructive'
                                                                                    : 'outline'
                                                                            "
                                                                            class="capitalize"
                                                                        >
                                                                            {{
                                                                                child.type.replace(
                                                                                    "workflow_",
                                                                                    "",
                                                                                )
                                                                            }}
                                                                        </Badge>
                                                                    </div>
                                                                    <p
                                                                        class="mt-1 text-xs text-iron-500"
                                                                    >
                                                                        <span
                                                                            v-if="
                                                                                child.actorName
                                                                            "
                                                                            >{{
                                                                                child.actorName
                                                                            }}</span
                                                                        >
                                                                        <span
                                                                            v-if="
                                                                                child.actorEmail
                                                                            "
                                                                        >
                                                                            —
                                                                            {{
                                                                                child.actorEmail
                                                                            }}</span
                                                                        >
                                                                        <span
                                                                            v-if="
                                                                                !child.actorName &&
                                                                                !child.actorEmail
                                                                            "
                                                                            >{{
                                                                                t(
                                                                                    "bookings.activity.system",
                                                                                )
                                                                            }}</span
                                                                        >
                                                                    </p>
                                                                    <div
                                                                        v-if="
                                                                            child.data
                                                                        "
                                                                        class="mt-2 rounded-lg bg-white w-[320px]"
                                                                    >
                                                                        <Accordion
                                                                            type="single"
                                                                            collapsible
                                                                            class="w-full"
                                                                        >
                                                                            <AccordionItem
                                                                                :value="
                                                                                    'child-payload-' +
                                                                                    child.id
                                                                                "
                                                                            >
                                                                                <AccordionTrigger
                                                                                    class="rounded-md border-iron-200 bg-iron-25 px-3 py-2 text-sm font-medium w-full border text-left"
                                                                                >
                                                                                    {{
                                                                                        t(
                                                                                            "bookings.activity.payload",
                                                                                        )
                                                                                    }}
                                                                                </AccordionTrigger>
                                                                                <AccordionContent>
                                                                                    <pre
                                                                                        class="mt-2 rounded-md border-iron-200 bg-iron-50 p-2 text-xs leading-relaxed text-iron-800 w-full max-w-full overflow-x-auto border"
                                                                                        >{{
                                                                                            pretty(
                                                                                                child.data,
                                                                                            )
                                                                                        }}</pre
                                                                                    >
                                                                                </AccordionContent>
                                                                            </AccordionItem>
                                                                        </Accordion>
                                                                    </div>
                                                                </div>
                                                                <span
                                                                    class="text-xs text-iron-500 shrink-0"
                                                                    >{{
                                                                        formatDayWithTime(
                                                                            child.createdAt,
                                                                        )
                                                                    }}</span
                                                                >
                                                            </div>
                                                        </div>
                                                    </div>
                                                </AccordionContent>
                                            </AccordionItem>
                                        </Accordion>
                                    </div>
                                </div>
                                <span class="text-sm text-iron-500 shrink-0">
                                </span>
                            </div>
                        </li>
                    </ol>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { isThisYear, isPast } from "date-fns";
import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";
import AppLayout from "../../../../Layouts/AppLayout.vue";
import BookingsHeaderLogo from "../Dashboard/components/BookingsHeaderLogo.svg";
import { Badge } from "@/components/ui/badge";
import {
    ExternalLink,
    Mail,
    Phone as PhoneIcon,
    MapPin,
    User as UserIcon,
    CheckCircle,
    XCircle,
    CalendarClock,
    X,
} from "lucide-vue-next";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    TableCellWithCopy,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import axios from "axios";
import route from "ziggy-js";
import useConfirm from "@/Pages/Zaptime/App/ConfirmDialog/composables/useConfirm";
import { router } from "@inertiajs/vue3";

const { t, locale } = useI18n();
const { format } = useLocalisedTzFormat();
const confirm = useConfirm();

type Activity = {
    id: number;
    type: string;
    message: string;
    status?: "success" | "fail" | string;
    actorType?: string | null;
    actorId?: number | null;
    actorName?: string | null;
    actorEmail?: string | null;
    data?: Record<string, unknown> | null;
    createdAt: string;
};

type Reservation = {
    id: number;
    uuid: string;
    eventTypeUuid: string;
    user_id: number;
    integration_event_id: string | null;
    type: string;
    email: string;
    phone: string | null;
    firstname: string;
    lastname: string;
    status: string;
    start: string;
    end: string;
    start_date: string;
    end_date: string;
    start_time: string;
    end_time: string;
    seats: number;
    data: unknown;
    googleMeetLink: string | null;
    zoomLink: string | null;
    meetingLink: string | null;
    provider?: string | null;
    organizer: string;
    organizer_name: string;
    event_title: string;
    customFieldValues: { label: string; value: string; type?: string }[];
    location?: { type: string; value: string };
};

const props = defineProps<{
    reservation: Reservation;
    activities: Activity[];
}>();

const orderedActivities = computed(() =>
    [...props.activities].sort(
        (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    ),
);

type ActivityGroup = {
    parent: Activity;
    children: Activity[];
};

const activityGroups = computed<ActivityGroup[]>(() => {
    const parents: Activity[] = [];
    const childrenByParentIndex: Record<number, Activity[]> = {};

    const isParentType = (type: string) =>
        ["prepared", "made", "rescheduled", "canceled", "cancelled"].includes(
            type,
        );

    const activities = orderedActivities.value;
    let currentParentIndex: number | null = null;

    for (const activity of activities) {
        if (isParentType(activity.type)) {
            parents.push(activity);
            currentParentIndex = parents.length;
            childrenByParentIndex[currentParentIndex] = [];
            continue;
        }

        // Treat workflow_* as children of the most recent parent event
        if (activity.type.startsWith("workflow_")) {
            if (currentParentIndex === null) {
                parents.push({
                    ...activity,
                    id: activity.id * 1000 + 1, // ensure uniqueness if needed
                    type: "prepared",
                    message: "Reservation activity",
                });
                currentParentIndex = parents.length - 1;
                childrenByParentIndex[currentParentIndex] = [];
            }
            childrenByParentIndex[currentParentIndex].push(activity);
            continue;
        }

        // Non-workflow, non-parent activities are listed as standalone parents
        parents.push(activity);
        currentParentIndex = parents.length - 1;
        childrenByParentIndex[currentParentIndex] = [];
    }

    return parents.map((parent, index) => ({
        parent,
        children: childrenByParentIndex[index] ?? [],
    }));
});

// Show newest groups first, and within each group show newest workflow actions first
const displayActivityGroups = computed<ActivityGroup[]>(() =>
    activityGroups.value
        .slice()
        .reverse()
        .map((group) => ({
            parent: group.parent,
            children: [...group.children].sort(
                (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime(),
            ),
        })),
);

function groupHasFailure(group: ActivityGroup): boolean {
    return group.children?.some((a) => isWorkflowFail(a));
}

const meetingUrl = computed(() => {
    return (
        props.reservation.meetingLink ||
        props.reservation.googleMeetLink ||
        props.reservation.zoomLink ||
        props.reservation.location?.value ||
        null
    );
});

const displayMeetingText = computed(() => {
    const url = meetingUrl.value;
    if (!url) return props.reservation.location?.value || "";
    try {
        const u = new URL(url);
        return u.hostname.replace("www.", "");
    } catch {
        return url;
    }
});

function bulletClass(type: string) {
    switch (type) {
        case "prepared":
            return "bg-blue-500";
        case "made":
            return "bg-green-500";
        case "rescheduled":
            return "bg-amber-500";
        case "canceled":
            return "bg-red-500";
        default:
            return "bg-iron-300";
    }
}

function pretty(data: unknown) {
    try {
        return JSON.stringify(data, null, 2);
    } catch {
        return String(data);
    }
}

function translateActivity(type: string, fallback: string): string {
    const key = `bookings.activity.messages.${type}`;
    const translated = t(key);
    // If translation returns the key itself, use fallback message
    if (translated === key) {
        return fallback;
    }
    return translated as unknown as string;
}

function isWorkflowFail(activity: Activity): boolean {
    return (
        activity.status === "fail" ||
        (typeof activity.type === "string" && activity.type.endsWith("_failed"))
    );
}

function formatCustomFieldValue(field: any): string {
    try {
        if (field?.type === "checkbox") {
            return field?.value ? t("yes") : t("no");
        }

        if (
            typeof field?.value === "string" &&
            field.value.startsWith("[") &&
            field.value.endsWith("]")
        ) {
            const parsed = JSON.parse(field.value);
            if (Array.isArray(parsed)) {
                return parsed.join(", ");
            }
        }

        if (Array.isArray(field?.value)) {
            return field.value.join(", ");
        }

        return String(field?.value ?? "");
    } catch {
        return String(field?.value ?? "");
    }
}

function formatDayWithTime(date: string) {
    if (locale.value === "cs") {
        return isThisYear(new Date(date))
            ? format(new Date(date), "EEEEEE, dd. MMMM HH:mm")
            : format(new Date(date), "EEEEEE, dd. MMMM yyyy HH:mm");
    } else {
        return isThisYear(new Date(date))
            ? format(new Date(date), "EE, d MMM, HH:mm")
            : format(new Date(date), "EE, d MMM yyyy, HH:mm");
    }
}

function formatDay(date: string) {
    if (locale.value === "cs") {
        return isThisYear(new Date(date))
            ? format(new Date(date), "EEEEEE, dd. MMMM")
            : format(new Date(date), "EEEEEE, dd. MMMM yyyy");
    } else {
        return isThisYear(new Date(date))
            ? format(new Date(date), "EE, d MMM")
            : format(new Date(date), "EE, d MMM yyyy");
    }
}
function formatFromTo(from: string, to: string) {
    if (locale.value === "en") {
        return `${format(new Date(from), "h:mm aaa")} - ${format(new Date(to), "h:mm aaa")}`;
    } else {
        return `${format(new Date(from), "H:mm")} - ${format(new Date(to), "H:mm")}`;
    }
}

const isCancellable = computed(() => {
    if (props.reservation.status === "canceled") {
        return false;
    }
    if (isPast(new Date(props.reservation.start_date))) {
        return false;
    }
    return true;
});

async function openRescheduleLink() {
    const url =
        route("public.event-types.show", {
            eventTypeUuid: props.reservation.eventTypeUuid,
        }) + `?reservationUuid=${props.reservation.uuid}`;
    router.visit(url);
}

const cancelEvent = async () => {
    const succes = await confirm.show({
        type: "danger",

        confirm: t("bookings.cancel_booking"),
        cancel: t("cancel"),

        header: t("bookings.cancel_booking"),
        message:
            t("bookings.are_you_sure") +
            "<br>" +
            t("bookings.cancel_description"),
    });

    if (succes) {
        axios
            .post(route("app.reservations.cancel", props.reservation.id))
            .then(() => {
                router.visit("/");
            });
    }
};
</script>

<style scoped>
ol li::before {
    content: "";
}
</style>
